/**
 * Kanban drag-over handler: column reordering and live card status updates.
 */

import { useCallback } from 'react';
import { isMissionOverSidebar } from '../../../../../utils/missionToSidebarBridge';
import {
  resolveColumnReorderTarget,
  reorderColumnsIfNeeded,
  resolveCardDropDestination,
  applyCardStatusUpdate,
  getWipMoveCheck,
} from './dragDropUtils';

const useDragOverHandler = ({
  activeCardId,
  columnIds,
  columns,
  taskIdsByColumn,
  tasksByStatus,
  statuses,
  findColumn,
  getPositionIndex,
  dropIndexRef,
  dropColumnRef,
  lastStatusChangeRef,
  onStatusChange,
  onSetStatusAutoSync,
  onColumnReorder,
  onWipLimitBlocked,
}) => {
  const handleDragOver = useCallback((event) => {
    const { active, over } = event;
    const activeData = active.data?.current;
    const overData = over?.data?.current;

    if (!over) return;

    if (activeData?.type === 'column') {
      const targetColumnId = resolveColumnReorderTarget(over, overData, findColumn, columnIds);
      reorderColumnsIfNeeded(columnIds, active.id, targetColumnId, onColumnReorder);
      return;
    }

    if (!activeCardId || isMissionOverSidebar()) return;

    const activeId = active.id;
    const { destColumnId, dropIndex } = resolveCardDropDestination(
      over.id,
      columnIds,
      findColumn,
      getPositionIndex,
      taskIdsByColumn,
    );

    dropIndexRef.current = dropIndex;
    dropColumnRef.current = destColumnId;

    const sourceColumnId = findColumn(activeId);
    if (!sourceColumnId || !destColumnId || sourceColumnId === destColumnId) return;

    if (
      lastStatusChangeRef.current.taskId === activeId
      && lastStatusChangeRef.current.columnId === destColumnId
    ) {
      return;
    }

    const destColumn = columns.find((c) => c.id === destColumnId);
    if (destColumn?.category === 'blocked') return;

    const wipCheck = getWipMoveCheck(destColumnId, tasksByStatus, statuses, activeId);
    if (!wipCheck.allowed) {
      onWipLimitBlocked?.(wipCheck.reason);
      return;
    }

    lastStatusChangeRef.current = { taskId: activeId, columnId: destColumnId };
    applyCardStatusUpdate(
      activeId,
      destColumnId,
      dropIndex,
      tasksByStatus,
      onStatusChange,
      onSetStatusAutoSync,
    );
  }, [
    activeCardId,
    columnIds,
    columns,
    findColumn,
    getPositionIndex,
    onStatusChange,
    onSetStatusAutoSync,
    onWipLimitBlocked,
    onColumnReorder,
    statuses,
    taskIdsByColumn,
    tasksByStatus,
    dropIndexRef,
    dropColumnRef,
    lastStatusChangeRef,
  ]);

  return handleDragOver;
};

export default useDragOverHandler;
